"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Icon } from "./Icon";
import { useTranslations } from "next-intl";
import type { Specialty } from "../[locale]/specialized-emr/emr_data";

const SPECIALTY_THEMES: Record<
  string,
  { accent: string; lightBg: string; border: string; badgeBg: string; badgeFg: string }
> = {
  cardiology: {
    accent: "#CC2D4A",
    lightBg: "#FFF1F2",
    border: "rgba(204,45,74,0.18)",
    badgeBg: "#FFE4E6",
    badgeFg: "#9F1239",
  },
  orthopaedics: {
    accent: "#1E7BD7",
    lightBg: "#F0F9FF",
    border: "rgba(30,123,215,0.18)",
    badgeBg: "#E0F2FE",
    badgeFg: "#075985",
  },
  obgyn: {
    accent: "#9C27B0",
    lightBg: "#FAF5FF",
    border: "rgba(156,39,176,0.18)",
    badgeBg: "#F3E8FF",
    badgeFg: "#6B21A8",
  },
  paediatrics: {
    accent: "#D97706",
    lightBg: "#FFFBEB",
    border: "rgba(217,119,6,0.2)",
    badgeBg: "#FEF3C7",
    badgeFg: "#92400E",
  },
  ophthalmology: {
    accent: "#0891B2",
    lightBg: "#ECFEFF",
    border: "rgba(8,145,178,0.18)",
    badgeBg: "#CFFAFE",
    badgeFg: "#155E75",
  },
  ent: {
    accent: "#16A34A",
    lightBg: "#F0FDF4",
    border: "rgba(22,163,74,0.18)",
    badgeBg: "#DCFCE7",
    badgeFg: "#166534",
  },
  oncology: {
    accent: "#7C3AED",
    lightBg: "#FDF4FF",
    border: "rgba(124,58,237,0.18)",
    badgeBg: "#FAE8FF",
    badgeFg: "#5B21B6",
  },
  nephrology: {
    accent: "#0369A1",
    lightBg: "#F0F9FF",
    border: "rgba(3,105,161,0.18)",
    badgeBg: "#E0F2FE",
    badgeFg: "#075985",
  },
  dermatology: {
    accent: "#D97706",
    lightBg: "#FFFBEB",
    border: "rgba(217,119,6,0.18)",
    badgeBg: "#FEF3C7",
    badgeFg: "#92400E",
  },
  general: {
    accent: "#053C61",
    lightBg: "#F8FAFC",
    border: "rgba(5,60,97,0.15)",
    badgeBg: "#F1F5F9",
    badgeFg: "#0F172A",
  },
};

function SpecialtyLiveWidget({ specialty }: { specialty: Specialty }) {
  const t = useTranslations("specializedEmr.widgets");
  const theme = SPECIALTY_THEMES[specialty.id] || SPECIALTY_THEMES.general;

  // Cardiology: Animated ECG wave + Live Heartbeat Pulse
  if (specialty.id === "cardiology") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md relative overflow-hidden">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("liveEcg")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
            {t("bpSinus")}
          </span>
        </div>
        <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="w-full h-14 overflow-visible my-2">
          <motion.path
            d="M 0 15 L 10 15 L 12 13 L 14 15 L 20 15 L 22 17 L 24 5 L 26 25 L 28 15 L 35 15 L 38 12 L 42 12 L 45 15 L 60 15 L 62 13 L 64 15 L 70 15 L 72 17 L 74 5 L 76 25 L 78 15 L 85 15 L 88 12 L 92 12 L 95 15 L 100 15"
            fill="none"
            stroke="#CC2D4A"
            strokeWidth="2.5"
            strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0.8 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
          />
        </svg>
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-slate-100 text-center">
          <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
            <div className="text-[10px] font-bold text-slate-400 uppercase">{t("heartRate")}</div>
            <div className="font-extrabold text-rose-600 text-sm mt-0.5">{t("bpm72")}</div>
          </div>
          <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
            <div className="text-[10px] font-bold text-slate-400 uppercase">{t("lvef")}</div>
            <div className="font-extrabold text-emerald-600 text-sm mt-0.5">{t("pct62")}</div>
          </div>
          <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100">
            <div className="text-[10px] font-bold text-slate-400 uppercase">{t("riskLevel")}</div>
            <div className="font-extrabold text-indigo-600 text-sm mt-0.5">{t("lowRisk")}</div>
          </div>
        </div>
      </div>
    );
  }

  // Orthopaedics: Animated Range-of-Motion Flexometer Bar
  if (specialty.id === "orthopaedics") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="bone" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("jointRom")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            {t("rightKnee")}
          </span>
        </div>
        <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3.5 my-2 border border-slate-100">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-slate-400 uppercase">{t("flexionAngle")}</span>
            <span className="text-lg font-black text-blue-600">{t("deg110Flexion")}</span>
          </div>
          <div className="w-28 bg-slate-200 rounded-full h-3 relative overflow-hidden">
            <motion.div
              className="h-3 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(30,123,215,0.6)]"
              animate={{ width: ["20%", "90%", "20%"] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
          <div className="bg-slate-50 rounded-xl p-2.5 text-center border border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t("extDeficit")}</span>
            <div className="font-extrabold text-slate-800 mt-0.5">{t("fullExt")}</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-2.5 text-center border border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t("painScore")}</span>
            <div className="font-extrabold text-emerald-600 mt-0.5">{t("mildPain")}</div>
          </div>
        </div>
      </div>
    );
  }

  // ObGyn: Animated Fetal Heart Rate Doppler Pulse
  if (specialty.id === "obgyn") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="baby" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("fetalMonitor")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
            {t("ga32")}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 my-2">
          <div className="bg-purple-50/70 rounded-xl p-3 text-center border border-purple-100 relative overflow-hidden">
            <div className="text-[10px] font-bold text-purple-600 uppercase">{t("fetalHr")}</div>
            <motion.div
              animate={{ scale: [1, 1.15, 1, 1.15, 1] }}
              transition={{ duration: 1.2, repeat: Infinity, times: [0, 0.15, 0.3, 0.45, 1], ease: "easeInOut" }}
              className="text-xl font-black text-purple-900 mt-0.5"
            >
              {t("bpm140")}
            </motion.div>
            <div className="text-[9px] font-bold text-emerald-600 mt-0.5">{t("reactiveTrace")}</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
            <div className="text-[10px] font-bold text-slate-400 uppercase">{t("edd")}</div>
            <div className="text-sm font-extrabold text-slate-900 mt-1">{t("oct24")}</div>
            <div className="text-[9px] font-bold text-slate-500 mt-0.5">{t("gravidaParity")}</div>
          </div>
        </div>
      </div>
    );
  }

  // Paediatrics: Animated Growth Percentile Path
  if (specialty.id === "paediatrics") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="child" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("whoGrowth")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {t("age24mo")}
          </span>
        </div>
        <div className="bg-amber-50/70 rounded-xl p-3 my-2 border border-amber-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold text-amber-700 uppercase">{t("weightPct")}</div>
            <div className="text-lg font-black text-amber-900">{t("pct98")}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold text-slate-400 uppercase">{t("heightLen")}</div>
            <div className="text-sm font-extrabold text-slate-900">{t("cm92")}</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-3 pt-2.5 border-t border-slate-100">
          <span className="text-slate-500 font-medium">{t("immStatus")}</span>
          <span className="font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-[10px]">
            {t("upToDate")}
          </span>
        </div>
      </div>
    );
  }

  // Ophthalmology: Animated OD/OS Visual Acuity Scanner
  if (specialty.id === "ophthalmology") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="eye" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("visualAcuity")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">
            {t("bilateralChart")}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 my-2">
          <div className="bg-cyan-50/50 rounded-xl p-3 border border-cyan-100">
            <div className="text-[10px] font-bold text-cyan-700 uppercase">{t("rightEye")}</div>
            <div className="text-lg font-black text-slate-900">{t("v2020")}</div>
            <div className="text-[10px] text-slate-500 font-medium mt-0.5">{t("iop14")}</div>
          </div>
          <div className="bg-cyan-50/50 rounded-xl p-3 border border-cyan-100">
            <div className="text-[10px] font-bold text-cyan-700 uppercase">{t("leftEye")}</div>
            <div className="text-lg font-black text-slate-900">{t("v2025")}</div>
            <div className="text-[10px] text-slate-500 font-medium mt-0.5">{t("iop15")}</div>
          </div>
        </div>
      </div>
    );
  }

  // ENT: Animated Audiometry Soundwave Bars
  if (specialty.id === "ent") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="ear" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("audiometry")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            {t("normalHearing")}
          </span>
        </div>
        <div className="flex items-end justify-between h-14 bg-emerald-50/50 rounded-xl p-3 my-2 border border-emerald-100 gap-1.5">
          {[40, 75, 55, 90, 65, 80, 45, 95, 60, 70].map((h, idx) => (
            <motion.div
              key={idx}
              className="w-full bg-emerald-500 rounded-t-sm shadow-[0_0_5px_rgba(16,185,129,0.5)]"
              animate={{ height: [`${h}%`, `${Math.max(10, h - 40)}%`, `${Math.min(100, h + 10)}%`, `${h}%`] }}
              transition={{ duration: 1.2 + idx * 0.15, repeat: Infinity, ease: "easeInOut" }}
            />
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
          <div className="bg-slate-50 rounded-xl p-2 text-center border border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t("pta")}</span>
            <div className="font-extrabold text-slate-800 mt-0.5">{t("db15")}</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-2 text-center border border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t("tympanometry")}</span>
            <div className="font-extrabold text-emerald-600 mt-0.5">{t("typeANormal")}</div>
          </div>
        </div>
      </div>
    );
  }

  // Oncology: Animated Chemotherapy Progress Bar
  if (specialty.id === "oncology") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="cell" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("cancerStaging")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200">
            {t("stageII")}
          </span>
        </div>
        <div className="bg-purple-50/50 rounded-xl p-3 my-2 border border-purple-100">
          <div className="flex justify-between items-center text-xs font-bold mb-1.5">
            <span className="text-purple-900">{t("chemoCycle")}</span>
            <span className="text-purple-600">{t("completed66")}</span>
          </div>
          <div className="w-full bg-purple-200 rounded-full h-2.5 overflow-hidden">
            <motion.div
              className="h-2.5 bg-purple-600 rounded-full relative overflow-hidden"
              initial={{ width: "0%" }}
              animate={{ width: "66%" }}
              transition={{ duration: 2, ease: "easeOut" }}
            >
              <motion.div
                className="absolute top-0 bottom-0 left-0 w-full bg-white/30"
                animate={{ x: ["-100%", "100%"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear", delay: 1 }}
              />
            </motion.div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
          <div className="bg-slate-50 rounded-xl p-2 text-center border border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t("regimen")}</span>
            <div className="font-extrabold text-slate-800 mt-0.5">{t("actProtocol")}</div>
          </div>
          <div className="bg-slate-50 rounded-xl p-2 text-center border border-slate-100">
            <span className="text-[9px] font-bold text-slate-400 uppercase">{t("treatmentResp")}</span>
            <div className="font-extrabold text-emerald-600 mt-0.5">{t("partialRemission")}</div>
          </div>
        </div>
      </div>
    );
  }

  // Nephrology: Animated Dialysis Session Progress
  if (specialty.id === "nephrology") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="droplets" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("ckdDialysis")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
            {t("ckdStage5")}
          </span>
        </div>
        <div className="bg-sky-50/50 rounded-xl p-3 my-2 border border-sky-100 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold text-sky-700 uppercase">{t("egfr")}</div>
            <div className="text-lg font-black text-sky-900">{t("ml12")}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold text-slate-400 uppercase">{t("ufTarget")}</div>
            <div className="text-sm font-extrabold text-slate-900">{t("ml2500")}</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-3 pt-2.5 border-t border-slate-100">
          <span className="text-slate-500 font-medium">{t("vascularAccess")}</span>
          <span className="font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-[10px]">
            {t("avFistula")}
          </span>
        </div>
      </div>
    );
  }

  // Dermatology: Animated Skin Lesion Scanner
  if (specialty.id === "dermatology") {
    return (
      <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
        <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <Icon name="skin" width={16} height={16} style={{ color: theme.accent }} />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("dermaImage")}</span>
          </div>
          <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {t("leftForearm")}
          </span>
        </div>
        <div className="bg-amber-50/40 rounded-xl p-3 my-2 border border-amber-100 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0 relative overflow-hidden">
            <motion.div
              className="absolute inset-0 bg-amber-300/20"
            />
            <motion.div
              className="absolute top-0 left-0 right-0 h-0.5 bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,1)]"
              animate={{ top: ["0%", "100%", "0%"] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            />
            <Icon name="eye" width={20} height={20} className="text-amber-700 relative z-10" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-bold text-slate-900 truncate">{t("lesionMorph")}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">{t("eryPapule")}</div>
          </div>
        </div>
        <div className="flex items-center justify-between text-xs mt-3 pt-2.5 border-t border-slate-100">
          <span className="text-slate-500 font-medium">{t("biopsyStatus")}</span>
          <span className="font-bold bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full text-[10px]">
            {t("benignFollowUp")}
          </span>
        </div>
      </div>
    );
  }

  // General Medicine / Default: Animated Vitals Monitor
  return (
    <div className="bg-white/95 text-slate-900 rounded-2xl p-5 border border-slate-200/90 shadow-md backdrop-blur-md">
      <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-3">
        <div className="flex items-center gap-2">
          <Icon name={specialty.icon} width={16} height={16} style={{ color: theme.accent }} />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            {t("vitalsDesk", { name: specialty.name })}
          </span>
        </div>
        <span className="text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
          {t("abdmSynced")}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 my-2">
        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 text-center">
          <div className="text-[9px] font-bold text-slate-400 uppercase">{t("bp")}</div>
          <div className="font-black text-slate-900 text-xs mt-0.5">120/80</div>
        </div>
        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 text-center">
          <div className="text-[9px] font-bold text-slate-400 uppercase">{t("spo2")}</div>
          <div className="font-black text-emerald-600 text-xs mt-0.5">99%</div>
        </div>
        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 text-center">
          <div className="text-[9px] font-bold text-slate-400 uppercase">{t("temp")}</div>
          <div className="font-black text-slate-900 text-xs mt-0.5">98.6 °F</div>
        </div>
      </div>
      <div className="flex items-center justify-between text-xs mt-3 pt-2.5 border-t border-slate-100">
        <span className="text-slate-400 font-medium">{t("encounterFlow")}</span>
        <span className="font-extrabold text-emerald-600">{t("faster")}</span>
      </div>
    </div>
  );
}

export function SpecialtyTabsClient({ specialties }: { specialties: Specialty[] }) {
  const tTabs = useTranslations("specializedEmr.widgets");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isInView, setIsInView] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const pillRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const elapsedRef = useRef(0);
  const lastIndexRef = useRef(activeIndex);

  const DURATION = 4500;

  const activeSpecialty = specialties[activeIndex] ?? specialties[0];
  const activeTheme = SPECIALTY_THEMES[activeSpecialty?.id] || SPECIALTY_THEMES.general;

  // IntersectionObserver to start auto-advance only when in view
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.15 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Smoothly center the active tab button inside the scroll container
  useEffect(() => {
    const activeBtn = pillRefs.current[activeIndex];
    const container = navContainerRef.current;
    if (activeBtn && container) {
      const containerWidth = container.clientWidth;
      const btnLeft = activeBtn.offsetLeft;
      const btnWidth = activeBtn.clientWidth;
      const targetLeft = btnLeft - containerWidth / 2 + btnWidth / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "smooth",
      });
    }
  }, [activeIndex]);

  // Reset progress bar on index change
  useEffect(() => {
    if (activeIndex !== lastIndexRef.current) {
      elapsedRef.current = 0;
      lastIndexRef.current = activeIndex;
      if (progressBarRef.current) {
        progressBarRef.current.style.width = "0%";
      }
    }
  }, [activeIndex]);

  // Auto-advance timer loop with progress bar animation
  useEffect(() => {
    if (!isInView || isHovered) return;

    let lastTime = performance.now();
    let frameId: number;

    const tick = (now: number) => {
      const delta = now - lastTime;
      lastTime = now;
      elapsedRef.current += delta;

      if (elapsedRef.current >= DURATION) {
        elapsedRef.current = 0;
        setActiveIndex((prev) => (prev + 1) % specialties.length);
        lastIndexRef.current = (activeIndex + 1) % specialties.length;
        if (progressBarRef.current) {
          progressBarRef.current.style.width = "0%";
        }
        return;
      }

      if (progressBarRef.current) {
        const pct = Math.min((elapsedRef.current / DURATION) * 100, 100);
        progressBarRef.current.style.width = `${pct}%`;
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [isInView, isHovered, activeIndex, specialties.length]);

  return (
    <div ref={containerRef} className="w-full flex flex-col items-center mt-8">
      {/* Horizontal Specialty Selector Tab Bar */}
      <div
        ref={navContainerRef}
        className="w-full overflow-x-auto no-scrollbar pb-3 mb-6 scroll-smooth"
      >
        <div className="flex items-center justify-start md:justify-center min-w-max gap-2 px-2">
          {specialties.map((sp, idx) => {
            const isActive = idx === activeIndex;
            const spTheme = SPECIALTY_THEMES[sp.id] || SPECIALTY_THEMES.general;
            return (
              <button
                key={sp.id}
                ref={(el) => {
                  pillRefs.current[idx] = el;
                }}
                onClick={() => {
                  setActiveIndex(idx);
                }}
                className={`relative px-4 py-2.5 rounded-2xl text-xs font-bold transition-colors duration-200 flex items-center gap-2 cursor-pointer shrink-0 border ${
                  isActive
                    ? "text-slate-900 border-slate-300 shadow-sm"
                    : "bg-white text-slate-600 hover:bg-slate-50 border-slate-200/80 shadow-xs"
                }`}
              >
                {isActive && (
                  <motion.div
                    layoutId="active-specialty-pill-bg"
                    className="absolute inset-0 rounded-2xl shadow-sm border pointer-events-none"
                    style={{
                      background: spTheme.lightBg,
                      borderColor: spTheme.accent,
                    }}
                    transition={{ type: "spring", stiffness: 350, damping: 28 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <Icon
                    name={sp.icon}
                    width={15}
                    height={15}
                    style={{ color: spTheme.accent }}
                  />
                  <span
                    className="whitespace-nowrap font-extrabold transition-colors duration-200"
                    style={{ color: isActive ? spTheme.accent : undefined }}
                  >
                    {sp.name}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Showcase Presentation Card */}
      <div
        className="w-full max-w-5xl mx-auto px-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="relative w-full rounded-3xl bg-white border border-slate-200/90 p-6 sm:p-9 overflow-hidden">
          {/* Animated Progress Timer Strip */}
          <div
            ref={progressBarRef}
            className="absolute top-0 left-0 h-1.5 z-20 rounded-t-3xl transition-all duration-75"
            style={{ background: activeTheme.accent, width: "0%" }}
          />

          {/* Ambient Top Glow */}
          <div
            className="absolute -top-32 -right-32 w-80 h-80 rounded-full blur-[100px] opacity-15 pointer-events-none transition-colors duration-500"
            style={{ background: activeTheme.accent }}
          />

          <AnimatePresence mode="wait">
            <motion.div
              key={activeSpecialty.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center"
            >
              {/* Left Column: Metadata */}
              <div className="lg:col-span-7 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-md shrink-0"
                    style={{ background: activeTheme.badgeBg, color: activeTheme.badgeFg }}
                  >
                    <Icon name={activeSpecialty.icon} width={24} height={24} />
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 block">
                      {tTabs("configuredWorkspace")}
                    </span>
                    <h3 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight leading-none mt-0.5">
                      {activeSpecialty.name}
                    </h3>
                  </div>
                </div>

                <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
                  {activeSpecialty.desc}
                </p>

                {/* Capabilities Grid */}
                <div className="mt-2">
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-slate-400 block mb-2.5">
                    {tTabs("keyCapabilities")}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {activeSpecialty.capabilities.map((cap) => (
                      <span
                        key={cap}
                        className="text-xs font-bold bg-slate-50 border border-slate-200/80 px-3 py-1.5 rounded-xl text-slate-800 flex items-center gap-2 shadow-2xs"
                      >
                        <span className="w-4 h-4 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                          <Icon name="check" width={10} height={10} />
                        </span>
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Live Interactive Specialty Widget in Light Theme */}
              <div className="lg:col-span-5 w-full">
                <SpecialtyLiveWidget specialty={activeSpecialty} />
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Footer Bar */}
          <div className="mt-8 pt-5 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-semibold text-slate-500">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>{tTabs("footerText")}</span>
            </div>

            <div className="flex items-center gap-1.5 text-slate-800 font-bold hover:text-blue-600 transition-colors cursor-pointer">
              <span>{tTabs("bookWalkthrough", { name: activeSpecialty.name })}</span>
              <Icon name="arrow" width={12} height={12} />
            </div>
          </div>
        </div>

        {/* Bottom Pagination Dots */}
        <div className="flex items-center justify-center space-x-2 mt-6">
          {specialties.map((sp, index) => (
            <button
              key={sp.id}
              onClick={() => setActiveIndex(index)}
              aria-label={`Select ${sp.name}`}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                activeIndex === index
                  ? "w-8 bg-slate-900 shadow-xs"
                  : "w-2 bg-slate-300 hover:bg-slate-400"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
